import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { prospectKey } from "queryKeys/prospectKey";
import {
  prospectListKey,
  prospectListItemsKey,
  prospectListCommentsKey,
  setProspectStatusMutationKey,
  updateProspectListStatusMutationKey,
  excludeProspectsMutationKey,
} from "queryKeys/prospectListKey";

const STALE_TIME = 24 * 60 * 60 * 1_000;

function logout() {
  window.location.reload();
}

const HANDLERS = {
  401: logout,
  403: logout,
};

export const HANDLER_STATUSES = Object.keys(HANDLERS);

function onError(error) {
  HANDLERS[error.response?.statusCode]?.(error);
}

// Explicit set (not a prefix check) so a future key that merely starts with
// "prospect-list" (e.g. a hypothetical "prospect-lists-index") doesn't
// silently inherit this carve-out.
const PROSPECT_LIST_QUERY_KEY_BASES = new Set([
  prospectListKey().at(0),
  prospectListItemsKey().at(0),
  prospectListCommentsKey().at(0),
]);

// `auth.guard.ts` signals an expired/invalid session as 403, not 401 (see
// `loadUserById` and the token-verification branch) — these are the exact
// messages it throws. A prospect-list 403 carrying one of these is a
// stranded session, not "you don't have access to this list"; it must fall
// through to the global logout()/reload below rather than being swallowed,
// or an expired-token PM would get stuck on a permanent, confidently-wrong
// "no access" message with no route back to login.
const AUTH_GUARD_FORBIDDEN_MESSAGES = new Set([
  "Invalid token",
  "User doesn't exist",
  "The user is not found.",
  "The user is not active.",
]);

function isAuthGuardForbidden(error) {
  return (
    error.response?.statusCode === 403 && AUTH_GUARD_FORBIDDEN_MESSAGES.has(error.response?.message)
  );
}

const queryCache = new QueryCache({
  onError(error, { queryKey }) {
    const base = queryKey.at(0);

    // Pre-existing carve-out for the single-prospect page: unconditional,
    // left as-is (out of scope here).
    if (base === prospectKey().at(0)) return;

    // Prospect-list queries: swallow a genuine resolver-authorization 403
    // (rendered inline by the page) but let an auth-guard 403 - an expired
    // or invalid session - fall through to the logout handler below.
    if (PROSPECT_LIST_QUERY_KEY_BASES.has(base) && !isAuthGuardForbidden(error)) return;

    onError(error);
  },
});

// Mutation-side twin of PROSPECT_LIST_QUERY_KEY_BASES. Same reasoning: an
// explicit set, not a prefix check.
const PROSPECT_LIST_MUTATION_KEY_BASES = new Set([
  setProspectStatusMutationKey().at(0),
  updateProspectListStatusMutationKey().at(0),
  excludeProspectsMutationKey().at(0),
]);

const mutationCache = new MutationCache({
  // `MutationCache.onError` is `(error, variables, context, mutation)` in
  // react-query v4; `mutation.options.mutationKey` is the only thing that
  // identifies WHICH mutation failed (there is no queryKey argument here),
  // which is why the two prospect-list mutation hooks set one.
  //
  // Deliberately NARROWER than the query-side carve-out above. The query side
  // swallows every prospect-list error except an auth-guard 403; here only a
  // 403 is ever swallowed, and only a 403 that is NOT one of the auth guard's
  // messages:
  //
  //   * 403 + auth-guard message  -> expired/invalid session. Falls through to
  //     logout(); the user must get back to the login screen.
  //   * 403 + anything else       -> an AUTHORIZATION verdict from the service
  //     layer ("Only a PM may set a prospect status.", "You do not have access
  //     to partner N."). The session is fine; reloading the page would just
  //     re-render the same armed controls and the user would never learn why
  //     nothing happened. Swallowed here so the mutation hook's own onError
  //     toasts `error.message` instead.
  //   * 401                       -> always a session problem, never an
  //     authorization verdict from this feature. Still logs out.
  //   * 404/409/5xx/network       -> never reached HANDLERS anyway; unchanged.
  //
  // (The armed-controls half of that data-coordinator case is fixed at source
  // in `ProspectListReview` — a DC now sees the list read-only. This is the
  // backstop for the paths that don't go through the page's own gating, e.g. a
  // PM whose partner link is revoked mid-session.)
  onError(error, variables, context, mutation) {
    const base = mutation?.options?.mutationKey?.at(0);
    const isAuthorizationForbidden =
      error.response?.statusCode === 403 && !isAuthGuardForbidden(error);

    if (isAuthorizationForbidden && PROSPECT_LIST_MUTATION_KEY_BASES.has(base)) return;

    onError(error);
  },
});

export default new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME,
    },
  },
});
